import React, { useEffect, useState, useCallback } from 'react';
import { Plus, Briefcase, Building2, BookText } from 'lucide-react';
import api from '../../config/api';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../store/authStore';

const statusColor: Record<string, string> = { ongoing: 'info', completed: 'success', terminated: 'danger' };

export default function Attachments() {
  const { user } = useAuthStore();
  const isStudent = user?.role === 'student';
  const isManager = ['admin', 'registrar', 'superadmin'].includes(user?.role || '');
  const [tab, setTab] = useState<'attachments' | 'employers'>('attachments');

  return (
    <div className="fade-in">
      <div className="page-header">
        <div><h1 className="page-title">{isStudent ? 'My Attachment' : 'Industrial Attachments'}</h1><p className="page-subtitle">Placements, logbooks, and supervisor evaluations</p></div>
      </div>

      {!isStudent && (
        <div className="tabs">
          <button className={`tab ${tab === 'attachments' ? 'active' : ''}`} onClick={() => setTab('attachments')}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Briefcase size={14} />Placements</span></button>
          {isManager && <button className={`tab ${tab === 'employers' ? 'active' : ''}`} onClick={() => setTab('employers')}><span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><Building2 size={14} />Employers</span></button>}
        </div>
      )}

      {(isStudent || tab === 'attachments') && <PlacementsPanel isStudent={isStudent} isManager={isManager} />}
      {!isStudent && tab === 'employers' && <EmployersPanel />}
    </div>
  );
}

function PlacementsPanel({ isStudent, isManager }: { isStudent: boolean; isManager: boolean }) {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [detailFor, setDetailFor] = useState<any>(null);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/attachments'); setItems(r.data.data || []); } finally { setLoading(false); }
  }, []);
  useEffect(() => { doFetch(); }, [doFetch]);

  return (
    <>
      {isManager && <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}><button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><Plus size={14} />Place Student</button></div>}
      <div className="card">
        <div className="table-container">
          <table>
            <thead><tr>{!isStudent && <th>Student</th>}<th>Employer</th><th>Supervisor</th><th>Dates</th><th>Status</th><th>Logbook</th><th></th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={7} style={{ textAlign: 'center', padding: 40 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></td></tr>
              : items.length === 0 ? <tr><td colSpan={7}><div className="empty-state"><Briefcase size={40} /><p>No attachment placements yet</p></div></td></tr>
              : items.map((a: any) => (
                <tr key={a.id}>
                  {!isStudent && <td>{a.student_first_name} {a.student_last_name}</td>}
                  <td style={{ fontWeight: 500 }}>{a.employer_name}<div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.industry}</div></td>
                  <td style={{ fontSize: 12 }}>{a.supervisor_name || '—'}</td>
                  <td style={{ fontSize: 12 }}>{new Date(a.start_date).toLocaleDateString()} – {new Date(a.end_date).toLocaleDateString()}</td>
                  <td><span className={`badge badge-${statusColor[a.status]}`} style={{ textTransform: 'capitalize' }}>{a.status}</span></td>
                  <td>{a.logbook_count} entries</td>
                  <td><button className="btn btn-secondary btn-sm" onClick={() => setDetailFor(a)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showAdd && <PlaceStudentModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); doFetch(); }} />}
      {detailFor && <AttachmentDetailModal attachment={detailFor} isStudent={isStudent} onClose={() => setDetailFor(null)} onChanged={doFetch} />}
    </>
  );
}

function PlaceStudentModal({ onClose, onSaved }: any) {
  const [employers, setEmployers] = useState<any[]>([]);
  const [searchStu, setSearchStu] = useState('');
  const [stuResults, setStuResults] = useState<any[]>([]);
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));

  useEffect(() => { api.get('/attachments/employers').then(r => setEmployers(r.data.data || [])); }, []);
  useEffect(() => {
    if (searchStu.length >= 2) api.get('/students', { params: { search: searchStu, limit: 10 } }).then(r => setStuResults(r.data.data.students));
    else setStuResults([]);
  }, [searchStu]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.student_id || !form.employer_id || !form.start_date || !form.end_date) { toast.error('All required fields must be filled'); return; }
    setLoading(true);
    try { await api.post('/attachments', form); toast.success('Student placed'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 480 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Place Student on Attachment</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">Student *</label>
              <input className="form-input" placeholder="Type name or student number..." value={searchStu} onChange={e => setSearchStu(e.target.value)} />
              {stuResults.length > 0 && (
                <div style={{ border: '1px solid var(--border)', borderRadius: 8, maxHeight: 180, overflowY: 'auto', marginTop: 4 }}>
                  {stuResults.map((s: any) => (
                    <div key={s.id} onClick={() => { set('student_id', s.id); setSearchStu(`${s.first_name} ${s.last_name} (${s.student_number})`); setStuResults([]); }} style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid var(--border)', fontSize: 13 }}>
                      <strong>{s.first_name} {s.last_name}</strong> — {s.student_number}
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="form-group"><label className="form-label">Employer *</label><select className="form-input form-select" required value={form.employer_id || ''} onChange={e => set('employer_id', e.target.value)}><option value="">Select employer...</option>{employers.map((e: any) => <option key={e.id} value={e.id}>{e.name}</option>)}</select></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Supervisor Name</label><input className="form-input" value={form.supervisor_name || ''} onChange={e => set('supervisor_name', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Supervisor Phone</label><input className="form-input" value={form.supervisor_phone || ''} onChange={e => set('supervisor_phone', e.target.value)} /></div>
            </div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Start Date *</label><input type="date" className="form-input" required value={form.start_date || ''} onChange={e => set('start_date', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">End Date *</label><input type="date" className="form-input" required value={form.end_date || ''} onChange={e => set('end_date', e.target.value)} /></div>
            </div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Place Student'}</button></div>
        </form>
      </div>
    </div>
  );
}

function AttachmentDetailModal({ attachment, isStudent, onClose, onChanged }: any) {
  const [logbook, setLogbook] = useState<any[]>([]);
  const [evaluations, setEvaluations] = useState<any[]>([]);
  const [showEntry, setShowEntry] = useState(false);
  const [loading, setLoading] = useState(true);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try {
      const [lb, ev] = await Promise.all([
        api.get(`/attachments/${attachment.id}/logbook`),
        api.get(`/attachments/${attachment.id}/evaluations`),
      ]);
      setLogbook(lb.data.data || []);
      setEvaluations(ev.data.data || []);
    } finally { setLoading(false); }
  }, [attachment.id]);
  useEffect(() => { doFetch(); }, [doFetch]);

  const setStatus = async (status: string) => {
    try { await api.patch(`/attachments/${attachment.id}/status`, { status }); toast.success('Status updated'); onChanged(); onClose(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 620 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ fontWeight: 600 }}>{attachment.employer_name}{!isStudent ? ` — ${attachment.student_first_name} ${attachment.student_last_name}` : ''}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '65vh', overflowY: 'auto' }}>
          {!isStudent && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setStatus('completed')}>Mark Completed</button>
              <button className="btn btn-secondary btn-sm" onClick={() => setStatus('terminated')}>Mark Terminated</button>
            </div>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h4 style={{ fontSize: 14, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}><BookText size={15} />Logbook</h4>
            {isStudent && <button className="btn btn-primary btn-sm" onClick={() => setShowEntry(true)}><Plus size={13} />Add Entry</button>}
          </div>
          {loading ? <div className="loader loader-dark" style={{ margin: '10px auto' }} /> : logbook.length === 0 ? (
            <div className="empty-state" style={{ padding: 20 }}><p>No logbook entries yet</p></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {logbook.map((l: any) => (
                <div key={l.id} style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong style={{ fontSize: 13 }}>Week {l.week_number} — {new Date(l.entry_date).toLocaleDateString()}</strong>
                    {l.reviewed_by_first_name && <span className="badge badge-success">Reviewed</span>}
                  </div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>{l.activities}</div>
                  {l.skills_gained && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>Skills: {l.skills_gained}</div>}
                </div>
              ))}
            </div>
          )}

          <h4 style={{ fontSize: 14, fontWeight: 600, marginBottom: 10 }}>Evaluations</h4>
          {evaluations.length === 0 ? <div className="empty-state" style={{ padding: 20 }}><p>No evaluations yet</p></div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {evaluations.map((e: any) => (
                <div key={e.id} style={{ padding: '10px 14px', background: 'var(--bg)', borderRadius: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <strong style={{ fontSize: 13, textTransform: 'capitalize' }}>{e.evaluator_type} Evaluation</strong>
                    {e.score != null && <span style={{ fontWeight: 700 }}>{e.score}/100</span>}
                  </div>
                  {e.comments && <div style={{ fontSize: 13, marginTop: 4 }}>{e.comments}</div>}
                </div>
              ))}
            </div>
          )}
        </div>
        <div className="modal-footer"><button className="btn btn-secondary" onClick={onClose}>Close</button></div>
      </div>
      {showEntry && <LogbookEntryModal attachmentId={attachment.id} onClose={() => setShowEntry(false)} onSaved={() => { setShowEntry(false); doFetch(); }} />}
    </div>
  );
}

function LogbookEntryModal({ attachmentId, onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.week_number || !form.entry_date || !form.activities) { toast.error('Week, date and activities are required'); return; }
    setLoading(true);
    try { await api.post(`/attachments/${attachmentId}/logbook`, form); toast.success('Entry submitted'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>New Logbook Entry</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Week Number *</label><input type="number" min={1} className="form-input" required value={form.week_number || ''} onChange={e => set('week_number', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Date *</label><input type="date" className="form-input" required value={form.entry_date || ''} onChange={e => set('entry_date', e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">Activities *</label><textarea className="form-input" rows={3} required value={form.activities || ''} onChange={e => set('activities', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Skills Gained</label><textarea className="form-input" rows={2} value={form.skills_gained || ''} onChange={e => set('skills_gained', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Submit'}</button></div>
        </form>
      </div>
    </div>
  );
}

function EmployersPanel() {
  const [items, setItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const doFetch = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/attachments/employers'); setItems(r.data.data || []); } finally { setLoading(false); }
  }, []);
  useEffect(() => { doFetch(); }, [doFetch]);

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}><button className="btn btn-primary btn-sm" onClick={() => setShowAdd(true)}><Plus size={14} />Add Employer</button></div>
      <div className="card">
        <div className="table-container">
          <table>
            <thead><tr><th>Name</th><th>Industry</th><th>Contact</th><th>Active Placements</th></tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={4} style={{ textAlign: 'center', padding: 40 }}><div className="loader loader-dark" style={{ margin: '0 auto' }} /></td></tr>
              : items.length === 0 ? <tr><td colSpan={4}><div className="empty-state"><Building2 size={40} /><p>No employers added yet</p></div></td></tr>
              : items.map((e: any) => (
                <tr key={e.id}>
                  <td style={{ fontWeight: 500 }}>{e.name}</td>
                  <td>{e.industry || '—'}</td>
                  <td style={{ fontSize: 12 }}>{e.contact_person}{e.contact_phone ? ` · ${e.contact_phone}` : ''}</td>
                  <td>{e.active_attachments}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {showAdd && <AddEmployerModal onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); doFetch(); }} />}
    </>
  );
}

function AddEmployerModal({ onClose, onSaved }: any) {
  const [form, setForm] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const set = (k: string, v: any) => setForm((f: any) => ({ ...f, [k]: v }));
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name) { toast.error('Name is required'); return; }
    setLoading(true);
    try { await api.post('/attachments/employers', form); toast.success('Employer added'); onSaved(); }
    catch (err: any) { toast.error(err?.response?.data?.message || 'Failed'); }
    finally { setLoading(false); }
  };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><h3 style={{ fontWeight: 600 }}>Add Employer</h3><button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 20 }}>×</button></div>
        <form onSubmit={submit}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div className="form-group"><label className="form-label">Name *</label><input className="form-input" required value={form.name || ''} onChange={e => set('name', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Industry</label><input className="form-input" value={form.industry || ''} onChange={e => set('industry', e.target.value)} /></div>
            <div className="form-group"><label className="form-label">Address</label><input className="form-input" value={form.address || ''} onChange={e => set('address', e.target.value)} /></div>
            <div className="grid-2" style={{ gap: 12 }}>
              <div className="form-group"><label className="form-label">Contact Person</label><input className="form-input" value={form.contact_person || ''} onChange={e => set('contact_person', e.target.value)} /></div>
              <div className="form-group"><label className="form-label">Contact Phone</label><input className="form-input" value={form.contact_phone || ''} onChange={e => set('contact_phone', e.target.value)} /></div>
            </div>
            <div className="form-group"><label className="form-label">Contact Email</label><input type="email" className="form-input" value={form.contact_email || ''} onChange={e => set('contact_email', e.target.value)} /></div>
          </div>
          <div className="modal-footer"><button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button><button type="submit" className="btn btn-primary" disabled={loading}>{loading ? <span className="loader" /> : 'Add'}</button></div>
        </form>
      </div>
    </div>
  );
}
